/**
 * Um upload: contagens, pendências por motivo e o botão de reprocessar.
 *
 * O motivo agrupado é o que transforma "3.412 linhas pendentes" — número que
 * ninguém ataca — em "217 produtos sem de-para", que é uma tarde de trabalho
 * com fim à vista. A separação entre acionável e informativo vem do backend
 * (`MOTIVOS_ACIONAVEIS`) e não é recalculada aqui: manter uma segunda cópia do
 * vocabulário é o defeito que produziu os quatro de-paras divergentes do R.
 */

import { Link, useNavigate, useParams } from 'react-router-dom'

import { Botao } from '../../components/Botao'
import { Cartao, Tile, Tiles } from '../../components/Cartao'
import { Aviso, Carregando, Erro, Vazio } from '../../components/Estado'
import { Pagina, Pilha } from '../../components/Pagina'
import { Tabela, type Coluna } from '../../components/Tabela'
import { contagem, dataHora } from '../../lib/formato'
import type { ResumoMotivo } from '../../lib/tipos'
import { useSessao } from '../../stores/sessao'
import { Selo } from './UploadsPage'
import { useRemoverUpload, useReprocessar, useResumoPendencias, useUpload } from './hooks'
import estilos from './Ingestao.module.css'

export function UploadDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const numero = Number(id)
  const navegar = useNavigate()
  const usuario = useSessao((estado) => estado.usuario)
  const podeReprocessar = Boolean(usuario?.podeIngerir || usuario?.isStaff)

  const upload = useUpload(numero)
  const resumo = useResumoPendencias(numero)
  const reprocessar = useReprocessar(numero)
  const remover = useRemoverUpload()

  if (Number.isNaN(numero)) return <Erro erro={new Error('Upload inválido.')} />
  if (upload.isPending) return <Carregando rotulo="Carregando o upload…" />
  if (upload.isError) return <Erro erro={upload.error} aoTentarDeNovo={() => upload.refetch()} />

  const dados = upload.data
  // A invariante do backend. Se parar de fechar, existe descarte silencioso —
  // exatamente o defeito que a Fase 2 eliminou. A tela avisa em vez de somar.
  const fecha = dados.linhasLidas === dados.linhasAceitas + dados.linhasPendentes

  const colunas: Coluna<ResumoMotivo>[] = [
    {
      chave: 'motivo',
      cabecalho: 'Motivo',
      celula: (item) => (
        <Link to={`/pendencias?upload=${numero}&motivo=${item.motivo}`}>{item.motivoLabel}</Link>
      ),
    },
    {
      chave: 'tipo',
      cabecalho: 'Resolve como',
      celula: (item) =>
        item.acionavel ? (
          <span className={estilos.acionavel}>Cadastrando um de-para</span>
        ) : (
          <span className={estilos.informativo}>
            Informativo — a célula veio vazia da coleta de campo
          </span>
        ),
    },
    {
      chave: 'total',
      cabecalho: 'Linhas',
      tipo: 'numero',
      largura: '110px',
      celula: (item) => contagem(item.total),
    },
  ]

  return (
    <Pagina
      titulo={dados.nomeOriginal}
      descricao={
        <>
          Enviado em {dataHora(dados.enviadoEm)} por {dados.enviadoPor}.
        </>
      }
      acoes={
        <>
          <Botao variante="sutil" onClick={() => navegar('/uploads')}>
            Voltar
          </Botao>
          {podeReprocessar && (
            <>
              <Botao
                variante="primario"
                onClick={() => reprocessar.mutate()}
                carregando={reprocessar.isPending}
                disabled={dados.linhasPendentes === 0}
              >
                Reprocessar pendentes
              </Botao>
              <Botao
                variante="perigo"
                carregando={remover.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Excluir "${dados.nomeOriginal}"? Isso apaga a importação inteira — os ` +
                        `${contagem(dados.fatosGravados)} fato(s) gravados, o staging e as ` +
                        `pendências. Não dá para desfazer.`,
                    )
                  ) {
                    remover.mutate(numero, { onSuccess: () => navegar('/uploads') })
                  }
                }}
              >
                Excluir upload
              </Botao>
            </>
          )}
        </>
      }
    >
      <Pilha>
        <div className={estilos.statusLinha}>
          <Selo status={dados.status} />
          {dados.mensagem && <span className={estilos.mensagem}>{dados.mensagem}</span>}
        </div>

        {!fecha && (
          <Aviso tom="critico">
            As contagens não fecham: {contagem(dados.linhasLidas)} lidas ≠{' '}
            {contagem(dados.linhasAceitas)} aceitas + {contagem(dados.linhasPendentes)} pendentes.
            Isso indica descarte silencioso na normalização e é um bug do backend, não do arquivo.
          </Aviso>
        )}

        {reprocessar.isError && <Erro erro={reprocessar.error} />}
        {remover.isError && <Erro erro={remover.error} />}
        {reprocessar.data && (
          <Aviso tom={reprocessar.data.aindaPendentes > 0 ? 'atencao' : 'bom'}>
            {contagem(reprocessar.data.resolvidas)} linha(s) resolvida(s),{' '}
            {contagem(reprocessar.data.aindaPendentes)} ainda pendente(s).
          </Aviso>
        )}

        <Tiles>
          <Tile rotulo="Linhas lidas" valor={contagem(dados.linhasLidas)} />
          <Tile rotulo="Aceitas" valor={contagem(dados.linhasAceitas)} tom="bom" />
          <Tile
            rotulo="Pendentes"
            valor={contagem(dados.linhasPendentes)}
            tom={dados.linhasPendentes > 0 ? 'critico' : 'neutro'}
          />
          <Tile
            rotulo="Fatos gravados"
            valor={contagem(dados.fatosGravados)}
            nota="Uma linha pode virar vários fatos"
          />
          <Tile
            rotulo="Já existentes"
            valor={contagem(dados.fatosJaExistentes)}
            nota="Sobrepostos pela chave natural"
          />
        </Tiles>

        {dados.abasIgnoradas.length > 0 && (
          <Aviso>
            Abas sem schema conhecido, registradas para que a ausência delas seja explicável:{' '}
            <strong>{dados.abasIgnoradas.join(', ')}</strong>.
          </Aviso>
        )}

        <Cartao
          titulo="Pendências por motivo"
          subtitulo={
            resumo.data
              ? `${contagem(resumo.data.total)} pendente(s), das quais ${contagem(
                  resumo.data.acionaveis,
                )} resolvem cadastrando algo.`
              : undefined
          }
        >
          {resumo.isPending && <Carregando />}
          {resumo.isError && <Erro erro={resumo.error} aoTentarDeNovo={() => resumo.refetch()} />}
          {resumo.data &&
            (resumo.data.porMotivo.length === 0 ? (
              <Vazio titulo="Nenhuma pendência aberta">
                Todas as linhas deste arquivo produziram pelo menos um fato.
              </Vazio>
            ) : (
              <Tabela
                colunas={colunas}
                linhas={resumo.data.porMotivo}
                chaveDe={(item) => item.motivo}
                rotulo="Pendências agrupadas por motivo"
              />
            ))}
        </Cartao>
      </Pilha>
    </Pagina>
  )
}
